import React, { useRef, useMemo } from "react";
import { scaleLinear } from "d3-scale";
import { max, bin } from "d3-array";
import { useChartDimensions } from "./common/utils";

const MARGIN = { top: 30, right: 30, bottom: 55, left: 70 };

const WidthDistGraph = ({ graphData }) => {
    const ref = useRef(null);
    const { width, height } = useChartDimensions(ref);

    const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
    const innerHeight = Math.max(0, (height || 450) - MARGIN.top - MARGIN.bottom);

    const { xScale, yScale, bins } = useMemo(() => {
        if (!graphData?.length) return {};
        const widths = graphData.map(d => d.widths).filter(w => w != null);
        const xScale = scaleLinear()
            .domain([Math.min(...widths), Math.max(...widths)])
            .nice()
            .range([0, innerWidth]);

        const binner = bin()
            .domain(xScale.domain())
            .thresholds(xScale.ticks(30));
        const bins = binner(widths);

        const yScale = scaleLinear()
            .domain([0, max(bins, b => b.length)])
            .nice()
            .range([innerHeight, 0]);

        return { xScale, yScale, bins };
    }, [graphData, innerWidth, innerHeight]);

    if (!bins || !xScale) {
        return <div ref={ref} style={{ height: 450 }}><p>No data</p></div>;
    }

    const xTicks = xScale.ticks(6);
    const yTicks = yScale.ticks(5);

    return (
        <div ref={ref} style={{ height: 450 }} className="graphborder graphborder-dashed border-cyan-500">
            <svg width={width} height={height || 450}>
                <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                    {yTicks.map(t => (
                        <line key={t} x1={0} x2={innerWidth} y1={yScale(t)} y2={yScale(t)}
                            stroke="#e5e7eb" strokeWidth={1} />
                    ))}

                    {bins.map((b, i) => {
                        const x = xScale(b.x0);
                        const w = Math.max(0, xScale(b.x1) - xScale(b.x0) - 1);
                        const y = yScale(b.length);
                        const h = innerHeight - y;
                        return (
                            <rect key={i} x={x} y={y} width={w} height={h}
                                fill="rgb(228,87,86)" fillOpacity={0.75}
                                stroke="white" strokeWidth={0.5} />
                        );
                    })}

                    <g transform={`translate(0,${innerHeight})`}>
                        <line x1={0} x2={innerWidth} stroke="#374151" />
                        {xTicks.map(t => (
                            <g key={t} transform={`translate(${xScale(t)},0)`}>
                                <line y2={5} stroke="#374151" />
                                <text y={18} textAnchor="middle" fontSize={10}>
                                    {t.toFixed(1)}
                                </text>
                            </g>
                        ))}
                        <text x={innerWidth / 2} y={42} textAnchor="middle" fontSize={11} fill="#374151">
                            Width (µs)
                        </text>
                    </g>

                    <g>
                        <line y1={0} y2={innerHeight} stroke="#374151" />
                        {yTicks.map(t => (
                            <g key={t} transform={`translate(0,${yScale(t)})`}>
                                <line x1={-5} stroke="#374151" />
                                <text x={-8} dy="0.32em" textAnchor="end" fontSize={10}>
                                    {t.toFixed(0)}
                                </text>
                            </g>
                        ))}
                        <text transform={`translate(-52,${innerHeight / 2}) rotate(-90)`}
                            textAnchor="middle" fontSize={11} fill="#374151">
                            Count
                        </text>
                    </g>
                </g>
            </svg>
        </div>
    );
};

export default WidthDistGraph;
